import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, GripVertical, MoveVertical, Save, X, Home } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useReorder } from '../../lib/useReorder';

export default function AdminHomeLayoutPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get('/home-sections');
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const dnd = useReorder(items, (it) => it.key);
  const visible = dnd.reorderMode ? dnd.orderDraft.map((k) => items.find((i) => i.key === k)).filter(Boolean) : items;

  async function persistOrder(ids) {
    try {
      await api.put('/admin/home-sections/reorder', { ids });
      await load();
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  async function toggleVisible(key, newVal) {
    try {
      await api.put(`/admin/home-sections/${key}`, { visible: newVal });
      setItems((prev) => prev.map((s) => s.key === key ? { ...s, visible: newVal } : s));
    } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  return (
    <div data-testid="admin-home-layout-page" className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Home size={22} />Diseño de la página principal</h1>
          <p className="text-charcoal-500 text-sm mt-1">Reordena y muestra/oculta secciones del Home sin tocar código.</p>
        </div>
        <div className="flex items-center gap-2">
          {dnd.reorderMode ? (
            <>
              <button onClick={() => dnd.saveOrder(persistOrder)} disabled={dnd.saving} data-testid="home-save-order-btn" className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-lg px-3 py-2 text-sm font-medium hover:bg-emerald-700">
                <Save size={14} />Guardar orden
              </button>
              <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50">
                <X size={14} />Cancelar
              </button>
            </>
          ) : (
            <button onClick={dnd.enterReorder} data-testid="home-reorder-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50">
              <MoveVertical size={14} />Reordenar
            </button>
          )}
        </div>
      </header>

      <div className="bg-cmic-50 border border-cmic-100 rounded-xl p-4 text-sm text-cmic-800 space-y-2">
        <div className="flex items-start gap-2">
          <span className="text-base">💡</span>
          <p><strong>¿Qué edita esta página?</strong> Solo gestiona las secciones del Home (página principal). Para editar el contenido de otras páginas, usa la sección correspondiente del menú:</p>
        </div>
        <ul className="text-xs ml-7 space-y-0.5 text-cmic-700">
          <li>• <strong>Quiénes Somos / Membresía / About / Reglamentos</strong>: edita los textos en <em>Traducciones</em></li>
          <li>• <strong>Consejo Directivo</strong>: usa <em>Consejo Directivo</em></li>
          <li>• <strong>Expresidentes</strong>: usa <em>Expresidentes</em></li>
          <li>• <strong>Noticias, Eventos, Periodicals</strong>: en sus secciones correspondientes</li>
        </ul>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin" /></div>
      ) : (
        <ul className="space-y-2">
          {visible.map((s, idx) => (
            <li key={s.key} draggable={dnd.reorderMode} {...(dnd.reorderMode ? dnd.dragProps(s.key) : {})} data-testid={`home-section-${s.key}`} className={`bg-white rounded-2xl border p-4 flex items-center gap-3 ${dnd.reorderMode ? 'cursor-move border-amber-200 bg-amber-50/50' : 'border-gray-200'} ${!s.visible ? 'opacity-60' : ''}`}>
              {dnd.reorderMode && <GripVertical size={16} className="text-charcoal-400 flex-shrink-0" />}
              <div className="w-9 h-9 rounded-lg bg-cmic-100 text-cmic-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-charcoal-800">{s.label}</div>
                <div className="text-xs text-charcoal-400 font-mono">{s.key}</div>
              </div>
              {!dnd.reorderMode && (
                <button
                  onClick={() => toggleVisible(s.key, !s.visible)}
                  data-testid={`home-toggle-${s.key}`}
                  title={s.visible ? 'Ocultar sección' : 'Mostrar sección'}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${s.visible ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-gray-100 text-charcoal-500 hover:bg-gray-200'}`}
                >
                  {s.visible ? <><Eye size={12} />Visible</> : <><EyeOff size={12} />Oculta</>}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
